import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { DashboardLayout } from '../components/DashboardLayout';
import axios from 'axios';
import { Pill, Heart, Droplets, Activity, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { MedicationsTab } from './clinical/MedicationsTab';
import { VitalsTab } from './clinical/VitalsTab';
import { OutputTab } from './clinical/OutputTab';
import { WoundsTab } from './clinical/WoundsTab';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ClinicalLogs() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('medications');
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [loading, setLoading] = useState(true);

  const [medications, setMedications] = useState([]);
  const [vitals, setVitals] = useState([]);
  const [outputs, setOutputs] = useState([]);
  const [wounds, setWounds] = useState([]);

  useEffect(() => { fetchClients(); }, []);
  useEffect(() => { if (selectedClient) fetchAll(); }, [selectedClient, activeTab]);

  const fetchClients = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/clinical/my-clients`, { withCredentials: true });
      setClients(data);
      if (data.length > 0) setSelectedClient(data[0].id);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const fetchAll = async () => {
    const cid = selectedClient;
    if (!cid) return;
    try {
      const [medRes, vitRes, outRes, wndRes] = await Promise.all([
        axios.get(`${API_URL}/api/clinical/medications?client_id=${cid}`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/clinical/vitals?client_id=${cid}`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/clinical/output?client_id=${cid}`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/clinical/wounds?client_id=${cid}`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setMedications(medRes.data);
      setVitals(vitRes.data);
      setOutputs(outRes.data);
      setWounds(wndRes.data);
    } catch (err) { console.error(err); }
  };

  const tabs = [
    { id: 'medications', label: 'Medications', icon: Pill, count: medications.length, color: '#00A6A0' },
    { id: 'vitals', label: 'Vital Signs', icon: Heart, count: vitals.length, color: '#FF6F61' },
    { id: 'output', label: 'Bowel & Urine', icon: Droplets, count: outputs.length, color: '#F59E0B' },
    { id: 'wounds', label: 'Wound Care', icon: Activity, count: wounds.length, color: '#8B5CF6' },
  ];

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="clinical-logs-page">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-heading font-bold text-foreground">
              <FileText className="w-7 h-7 inline mr-3 text-[#00A6A0]" />Clinical Logs
            </h1>
            <p className="text-muted-foreground mt-1">Medications, vitals, output, and wound care documentation</p>
          </div>
          <div className="flex items-center gap-3">
            <Label className="text-muted-foreground text-sm">Client:</Label>
            <select
              value={selectedClient}
              onChange={e => setSelectedClient(e.target.value)}
              className="bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm min-w-[200px]"
              data-testid="client-select"
            >
              <option value="">Select client...</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
        </div>

        <div className="flex gap-2 flex-wrap">
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <Button
                key={tab.id}
                size="sm"
                variant={activeTab === tab.id ? 'default' : 'outline'}
                className={activeTab === tab.id ? 'text-white' : 'border-border text-muted-foreground'}
                style={activeTab === tab.id ? { backgroundColor: tab.color } : {}}
                onClick={() => setActiveTab(tab.id)}
                data-testid={`tab-${tab.id}`}
              >
                <Icon className="w-4 h-4 mr-1.5" />{tab.label} ({tab.count})
              </Button>
            );
          })}
        </div>

        {!selectedClient ? (
          <Card className="glass-card border-border"><CardContent className="p-12 text-center"><p className="text-muted-foreground">Select a client to view clinical logs</p></CardContent></Card>
        ) : (
          <>
            {activeTab === 'medications' && <MedicationsTab medications={medications} clientId={selectedClient} onRefresh={fetchAll} user={user} />}
            {activeTab === 'vitals' && <VitalsTab vitals={vitals} clientId={selectedClient} onRefresh={fetchAll} user={user} />}
            {activeTab === 'output' && <OutputTab outputs={outputs} clientId={selectedClient} onRefresh={fetchAll} user={user} />}
            {activeTab === 'wounds' && <WoundsTab wounds={wounds} clientId={selectedClient} onRefresh={fetchAll} user={user} />}
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
